/**
 * ChapterAssignmentPanel — renders inside the student Learn view once the
 * chapter quiz has been passed. The full flow:
 *
 *   [Quiz passed]  →  Assignment card visible
 *   [Not started]  →  "Start assignment" button
 *   [Locked timer] →  Countdown "Upload unlocks in Xh Ym Zs" + upload button
 *                     disabled with tooltip
 *   [Unlocked]     →  File picker + Submit button
 *   [Submitted]    →  Success state with next-chapter prompt
 *   [Graded]       →  Success state + grade + feedback (optional)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    ClipboardList, FileText, Loader2, Lock, Upload, CheckCircle2,
    ShieldCheck, Download,
} from "lucide-react";

function fmtError(e, fb) { return e?.response?.data?.detail || e?.message || fb; }

function fmtCountdown(ms) {
    if (ms <= 0) return "0h 00m 00s";
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}h ${String(m).padStart(2, "0")}m ${String(sec).padStart(2, "0")}s`;
}

export default function ChapterAssignmentPanel({ chapter, onSubmitted }) {
    const [assignment, setAssignment] = useState(null);
    const [submission, setSubmission] = useState(null);
    const [lockedReason, setLockedReason] = useState(null);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [clientNow, setClientNow] = useState(Date.now());
    const clockRef = useRef(null);
    const fileInputRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/me/chapters/${chapter.id}/assignment`);
            setAssignment(data.assignment);
            setSubmission(data.submission);
            setLockedReason(data.locked_reason || null);
        } catch (e) {
            // 404s / non-fatal — chapter simply has no assignment
            setAssignment(null);
            setSubmission(null);
        } finally {
            setLoading(false);
        }
    }, [chapter.id]);

    useEffect(() => { load(); }, [load]);

    // Live countdown ticker — updates every second when we have a locked
    // submission that hasn't been submitted yet.
    useEffect(() => {
        if (!submission || submission.submitted_at) {
            if (clockRef.current) clearInterval(clockRef.current);
            return;
        }
        setClientNow(Date.now());
        clockRef.current = setInterval(() => setClientNow(Date.now()), 1000);
        return () => { if (clockRef.current) clearInterval(clockRef.current); };
    }, [submission]);

    const start = async () => {
        setStarting(true);
        try {
            const { data } = await api.post(`/me/chapters/${chapter.id}/assignment/start`);
            setSubmission(data);
            toast.success("Assignment timer started");
        } catch (e) {
            toast.error(fmtError(e, "Could not start the assignment"));
        } finally {
            setStarting(false);
        }
    };

    const upload = async (file) => {
        if (!file) return;
        const allowed = ["application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/zip",
            "application/x-zip-compressed",
        ];
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        if (!["pdf", "doc", "docx", "zip"].includes(ext)) {
            toast.error("Please upload a PDF, DOC, DOCX, or ZIP file.");
            return;
        }
        if (file.size > 20 * 1024 * 1024) {
            toast.error("File is larger than 20 MB.");
            return;
        }
        if (file.type && !allowed.includes(file.type) && file.type !== "application/octet-stream") {
            // extension check already passed; some browsers send generic types
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(
                `/me/chapters/${chapter.id}/assignment/submit`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setSubmission(data);
            toast.success("Assignment submitted. Next chapter is unlocked.");
            onSubmitted?.();
        } catch (e) {
            toast.error(fmtError(e, "Could not submit assignment"));
        } finally {
            setUploading(false);
        }
    };

    const downloadBrief = async () => {
        if (!assignment?.attachment) return;
        try {
            const res = await api.get(
                `/me/assignments/${assignment.id}/attachment`,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = assignment.attachment.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(fmtError(e, "Could not download brief"));
        }
    };

    const unlockRemaining = useMemo(() => {
        if (!submission?.unlock_at) return null;
        const unlockMs = new Date(submission.unlock_at).getTime();
        return unlockMs - clientNow;
    }, [submission?.unlock_at, clientNow]);

    const isUnlocked = unlockRemaining !== null && unlockRemaining <= 0;
    const alreadySubmitted = !!submission?.submitted_at;

    if (loading) {
        return (
            <div className="my-6 p-6 rounded-xl border border-border bg-muted/10 text-center text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            </div>
        );
    }

    if (!assignment) return null;  // No assignment configured for this chapter

    if (lockedReason === "quiz_not_passed") {
        return (
            <motion.section
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="my-6 p-6 rounded-xl border border-dashed border-border bg-muted/10"
                data-testid="assignment-quiz-lock"
            >
                <div className="flex items-center gap-3 text-muted-foreground">
                    <Lock className="h-5 w-5" />
                    <div className="text-sm">
                        Pass the chapter quiz above to unlock this assignment.
                    </div>
                </div>
            </motion.section>
        );
    }

    return (
        <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="my-6 rounded-xl border border-primary/20 bg-white shadow-sm overflow-hidden"
            data-testid="assignment-panel"
        >
            <div className="p-5 border-b border-border bg-[#FBF5E8]/60">
                <div className="flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-[#C79520]" />
                    <span className="text-[11px] uppercase tracking-[0.14em] text-[#C79520] font-semibold">
                        Chapter assignment
                    </span>
                </div>
                <h3 className="font-display text-2xl text-[#134B30] mt-1">
                    {assignment.title}
                </h3>
                <div className="text-xs text-muted-foreground mt-1">
                    Worth {assignment.max_marks} marks
                    {assignment.pass_mark ? ` · Pass mark ${assignment.pass_mark}` : ""}
                    {" · "}
                    Recommended study time: ~{assignment.estimated_hours} hours
                </div>
            </div>

            <div className="p-5 space-y-4">
                {assignment.description && (
                    <div className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed" data-testid="assignment-description">
                        {assignment.description}
                    </div>
                )}
                {assignment.rubric && (
                    <div className="border-l-2 border-[#C79520] pl-3">
                        <div className="text-[11px] uppercase tracking-[0.14em] text-[#C79520] font-semibold">
                            Grading rubric
                        </div>
                        <div className="text-sm text-muted-foreground whitespace-pre-wrap mt-1">
                            {assignment.rubric}
                        </div>
                    </div>
                )}
                {assignment.attachment && (
                    <div className="flex items-center gap-2 p-3 bg-[#FBF5E8]/60 rounded border border-[#E1DAC5]">
                        <FileText className="h-4 w-4 text-[#134B30]" />
                        <span className="text-sm flex-1 truncate">
                            {assignment.attachment.filename}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={downloadBrief}
                            data-testid="assignment-brief-download"
                        >
                            <Download className="h-3 w-3 mr-1" />
                            Download brief
                        </Button>
                    </div>
                )}
            </div>

            {/* --- Action states --- */}
            <div className="p-5 pt-0">
                {!submission && (
                    <div className="rounded-lg border border-dashed border-primary/30 p-4 flex items-center justify-between gap-3">
                        <div className="text-sm text-muted-foreground">
                            Ready when you are. Click below to begin — the upload window
                            opens after your recommended study time.
                        </div>
                        <Button
                            onClick={start}
                            disabled={starting}
                            className="bg-[#134B30] hover:bg-[#0E3A24] text-white gap-2"
                            data-testid="assignment-start-btn"
                        >
                            {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />}
                            Start assignment
                        </Button>
                    </div>
                )}

                {submission && !alreadySubmitted && !isUnlocked && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="assignment-locked-countdown">
                        <div className="flex items-start gap-3">
                            <ShieldCheck className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <div className="text-sm font-semibold text-amber-900">
                                    Take your time — this assignment needs careful, considered work.
                                </div>
                                <div className="text-xs text-amber-800 mt-1">
                                    Upload will unlock in{" "}
                                    <span className="font-mono font-bold text-base" data-testid="assignment-countdown">
                                        {fmtCountdown(unlockRemaining ?? 0)}
                                    </span>
                                    . You can safely close this page and come back — the timer runs
                                    on our servers.
                                </div>
                            </div>
                        </div>
                        <Button
                            disabled
                            className="mt-4 w-full bg-muted text-muted-foreground cursor-not-allowed"
                            data-testid="assignment-upload-locked"
                        >
                            <Lock className="h-4 w-4 mr-2" />
                            Upload unlocks in {fmtCountdown(unlockRemaining ?? 0)}
                        </Button>
                    </div>
                )}

                {submission && !alreadySubmitted && isUnlocked && (
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
                        <div className="text-sm font-semibold text-[#134B30] mb-3">
                            Your assignment is ready to upload.
                        </div>
                        <div className="text-xs text-muted-foreground mb-3">
                            Accepted files: PDF, DOC, DOCX, or ZIP · max 20&nbsp;MB · single upload only.
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.doc,.docx,.zip,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/zip"
                            className="hidden"
                            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                            data-testid="assignment-file-input"
                        />
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="w-full bg-[#134B30] hover:bg-[#0E3A24] text-white gap-2"
                            data-testid="assignment-upload-btn"
                        >
                            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                            Upload & submit assignment
                        </Button>
                    </div>
                )}

                {alreadySubmitted && (
                    <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4" data-testid="assignment-submitted">
                        <div className="flex items-center gap-2 text-emerald-800">
                            <CheckCircle2 className="h-5 w-5" />
                            <span className="text-sm font-semibold">
                                Assignment submitted. Next chapter unlocked.
                            </span>
                        </div>
                        {submission.file?.filename && (
                            <div className="text-xs text-emerald-700 mt-2 inline-flex items-center gap-1.5">
                                <FileText className="h-3 w-3" />
                                {submission.file.filename}
                                <span className="text-emerald-600/70">
                                    ({Math.round((submission.file.size || 0) / 1024)} KB)
                                </span>
                            </div>
                        )}
                        {submission.status === "graded" && (
                            <div className="mt-3 pt-3 border-t border-emerald-200 text-sm text-emerald-900">
                                <div>
                                    <strong>Grade:</strong> {submission.marks}/{assignment.max_marks}
                                </div>
                                {submission.feedback && (
                                    <div className="mt-1 text-xs text-emerald-800">
                                        <strong>Feedback:</strong> {submission.feedback}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </motion.section>
    );
}
